<script setup lang="ts">
import InventoryCalendar from './InventoryCalendar.vue';
import PriceCardPrice from './PriceCardPrice.vue';
import type HotelPackage from '@itinerary/hotel/HotelPackage';
import type HotelPriceClass from '@itinerary/hotel/HotelPriceClass';

const { item, nights, hideReserve, floating } = defineProps<IProps>();
const emit = defineEmits<{
    select: [];
}>();
const invoiceStore = useInvoiceStore();
const packageSelectStore = usePackageSelectStore();
interface IProps {
    item: HotelPriceClass;
    nights: number;
    hideReserve?: boolean;
    floating?: boolean;
}
const isLocked = useScrollLock(
    typeof document === 'undefined' ? null : document.body
);
const { open } = useModal({
    component: InventoryCalendar,
    attrs: {
        roomId: item.id ?? 'room',
    },
});
const gtm = useGtm();
const { featureFlags } = storeToRefs(usePostHog());
const config = useRuntimeConfig();
const title = computed(() => {
    const services = item.roomServiceTitle.filter(Boolean);
    return services.join(' و ') || 'بدون وعده غذایی';
});
const currentValue = computed(() => {
    const current = packageSelectStore.selectedPackages[0] as HotelPackage;
    if (current.selectedPrices?.length) {
        return current.selectedPrices.reduce((count, curr) => {
            if (curr.id === item.id) {
                return count + 1;
            } else {
                return count;
            }
        }, 0);
    }
    return 0;
});
const showExtraBed = computed(
    () =>
        item.hasExtraBed &&
        currentValue.value > 0 &&
        !hideReserve &&
        !packageSelectStore.currentPackageIsFloating
);
const extraBedCapacity = computed(
    () => currentValue.value * item.extraBedCapacity
);
const extraBedCount = computed(() =>
    packageSelectStore.getExtraPersonCounts(item)
);
const breakDownRoomPrices = computed(() =>
    item.breakDownRoomPrices.map(bd => ({
        payable: formatPrice(bd.salePrice || 0),
        undiscounted:
            item.discountedPrice &&
            bd.boardPrice &&
            bd.boardPrice > (bd.salePrice || 0)
                ? formatPrice(bd.boardPrice)
                : undefined,
        date: formatDate(bd.date!),
    }))
);
const changeExtraPerson = (count: number) => {
    packageSelectStore.changeExtraPerson({
        count,
        price: item,
    });
    invoiceStore.loadPreInvoice(packageSelectStore.selectedPackages[0]);
};
const selectClass = (count: number) => {
    if (
        count === 1 &&
        !packageSelectStore.currentlySelectedPackage.selectedPrices?.length
    ) {
        emit('select');
    }
    packageSelectStore.selectPriceClassForPackageIndex({
        priceClass: item,
        count,
        packageIndex: 0,
    });
    invoiceStore.loadPreInvoice(packageSelectStore.selectedPackages[0]);
};
const formatDate = (date: string) =>
    dayConfig.js(date).format(formatter({ format: 'long' }));
const formatPrice = (value: number | string) =>
    formatMoney(value, {
        includeUnit: false,
    });
const showInventoryCalendar = async () => {
    gtm?.trackEvent({
        event: 'show-hotel-inventory-calendar',
    });
    isLocked.value = true;
    await open();
    isLocked.value = false;
};
const isFloatingEnabled = computed(() => {
    if (config.public.runEnv === 'prod') {
        return featureFlags.value?.['hotel-floating-enabled'];
    } else {
        return true;
    }
});
const floatingMode = computed(() => {
    if (featureFlags.value?.['show-floating-all']) {
        return item.isFloating;
    } else {
        return floating;
    }
});
</script>

<template>
    <div class="price-card-wrapper">
        <div class="price-card-container" :class="{ full: !item.capacity }">
            <mr-typography
                :size="{ desktop: 'large', mobile: 'medium' }"
                weight="500"
                grow
                color="var(--cool-gray-600)"
                class="title"
            >
                {{ title }}
            </mr-typography>
            <slot />
            <slot v-if="!hideReserve" name="reserve">
                <div v-if="item.capacity > 0" class="reserve-container">
                    <PriceCardPrice :item :break-down-room-prices :nights />
                    <mr-counter
                        :model-value="currentValue"
                        unit="اتاق"
                        :max="item.capacity"
                        class="counter"
                        start-button
                        :size="{ mobile: 'medium', desktop: 'big' }"
                        @update:model-value="selectClass"
                    />
                </div>
                <div v-else-if="floatingMode" class="reserve-container">
                    <mr-typography
                        v-if="!item.singlePayable"
                        size="large"
                        weight="500"
                        color="var(--cool-gray-600)"
                        class="full"
                    >
                        نیازمند استعلام
                    </mr-typography>
                    <PriceCardPrice
                        v-else
                        :item
                        :break-down-room-prices
                        :nights
                    />
                    <mr-counter
                        :model-value="currentValue"
                        :disabled="!isFloatingEnabled"
                        unit="اتاق"
                        class="counter"
                        start-button
                        :size="{ mobile: 'medium', desktop: 'big' }"
                        @update:model-value="selectClass"
                    />
                </div>
                <div v-else class="full-capacity">
                    <mr-typography
                        size="large"
                        weight="500"
                        color="var(--cool-gray-600)"
                        class="full"
                    >
                        تکمیل ظرفیت
                    </mr-typography>
                    <mr-button
                        id="click room calender-hdp"
                        class="counter"
                        variant="bordered"
                        size="big"
                        :disabled="!item.id"
                        @click="showInventoryCalendar"
                    >
                        تقویم موجودی
                    </mr-button>
                </div>
            </slot>
        </div>
        <transition>
            <div v-if="showExtraBed" class="choose-extra-bed-container">
                <mr-typography
                    :size="{
                        desktop: 'large',
                        mobile: 'medium',
                    }"
                    weight="500"
                    color="var(--cool-gray-600)"
                    grow
                    class="extra-bed-title"
                >
                    مجموع تخت اضافه
                </mr-typography>
                <mr-price-re
                    :price="item.extraBedPrice"
                    :type="{ desktop: 'small', mobile: 'medium' }"
                    prefix="هر تخت"
                    class="bed-price"
                />
                <mr-counter
                    :model-value="extraBedCount"
                    unit="تخت اضافه"
                    :max="extraBedCapacity"
                    class="counter"
                    :size="{ mobile: 'medium', desktop: 'big' }"
                    @update:model-value="changeExtraPerson"
                />
            </div>
        </transition>
    </div>
</template>

<style lang="scss" scoped>
.price-card-wrapper {
    transition: all 0.2s ease;
    @include v-transition-scale-y;
    .price-card-container {
        display: flex;
        align-items: center;
        @include mobile-only {
            flex-wrap: wrap;
            padding-inline: 16px;
        }
        .title {
            @include mobile-only {
                color: usePalette($p-cool-gray, $p-700);
                flex-basis: 100%;
                max-width: 100%;
            }
        }
        .reserve-container {
            display: flex;
            align-items: center;
            column-gap: 20px;
            flex-wrap: wrap;
            @include mobile-only {
                flex-direction: column;
                width: 100%;
            }
            .price-container {
                display: flex;
                align-items: flex-end;
                flex-direction: column;
                @include mobile-only {
                    flex-direction: row;
                    align-items: center;
                    justify-content: space-between;
                    width: 100%;
                    flex-basis: 100%;
                    max-width: 100%;
                }
                .price {
                    display: flex;
                    white-space: nowrap;
                    align-items: center;
                    .price-info-icon {
                        position: relative;
                        .info-wrapper {
                            display: none;
                        }
                        &:focus,
                        &:hover {
                            .info-wrapper {
                                display: flex;
                                flex-direction: column;
                                transform: translateX(180px);
                                z-index: 1;
                                border-radius: 12px;
                                position: absolute;
                                width: 210px;
                                background-color: white;
                                @include shadow();
                                padding: 12px;
                                .info {
                                    display: flex;
                                    .info-price {
                                        display: flex;
                                        .price {
                                            margin-right: 8px;
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
            .counter {
                width: 220px;
                @include mobile-only {
                    width: 100%;
                    margin-top: 8px;
                }
            }
        }
        .full-capacity {
            display: flex;
            align-items: center;
            justify-content: space-between;
            column-gap: 20px;
            @include mobile-only {
                width: 100%;
                column-gap: 8px;
            }
            .full {
                @include mobile-only {
                    flex-grow: 1;
                }
            }
            .counter {
                width: 220px;
                @include mobile-only {
                    flex-grow: 1;
                    width: auto;
                    margin-top: 8px;
                }
            }
        }
    }
    .choose-extra-bed-container {
        display: flex;
        align-items: center;
        justify-content: space-between;
        column-gap: 20px;
        @include mobile-only {
            flex-wrap: wrap;
            padding-inline: 16px;
        }
        .extra-bed-title {
            @include mobile-only {
                flex-basis: 100%;
                max-width: 100%;
                color: usePalette($p-cool-gray, $p-700);
            }
        }
        .bed-price {
            @include mobile-only {
                justify-content: flex-end;
                flex-basis: 100%;
            }
        }
        .counter {
            width: 220px;
            @include mobile-only {
                flex-basis: 100%;
                max-width: 100%;
                width: 100%;
                margin-top: 8px;
            }
        }
    }
}
</style>
